import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { BulbOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  createFeatureRequest,
  getFeatureRequests,
  updateFeatureRequestStatus,
} from '../api/client';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function FeatureRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [form] = Form.useForm();

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFeatureRequests();
      setRequests(data.requests || []);
    } catch {
      message.error('Failed to load feature requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await createFeatureRequest(values.title.trim(), (values.description || '').trim());
      message.success('Feature request submitted');
      form.resetFields();
      fetchRequests();
    } catch {
      message.error('Failed to submit feature request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (record) => {
    const newStatus = record.status === 'completed' ? 'open' : 'completed';
    setTogglingId(record.request_id);
    try {
      await updateFeatureRequestStatus(record.request_id, newStatus);
      setRequests((prev) =>
        prev.map((r) =>
          r.request_id === record.request_id ? { ...r, status: newStatus } : r
        )
      );
      message.success(newStatus === 'completed' ? 'Marked as completed' : 'Re-opened');
    } catch {
      message.error('Failed to update request');
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      title: '',
      key: 'done',
      width: 40,
      render: (_, record) => (
        <Checkbox
          checked={record.status === 'completed'}
          onChange={() => handleToggle(record)}
          disabled={togglingId === record.request_id}
        />
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <Text
          delete={record.status === 'completed'}
          style={{ color: record.status === 'completed' ? 'inherit' : undefined }}
        >
          {title}
        </Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">—</Text>,
    },
    {
      title: 'Requested By',
      dataIndex: 'requested_by',
      key: 'requested_by',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Requested',
      dataIndex: 'requested_at',
      key: 'requested_at',
      width: 160,
      render: (ts) =>
        ts ? (
          <Tooltip title={new Date(ts).toLocaleString()}>
            <Text type="secondary">{new Date(ts).toLocaleDateString()}</Text>
          </Tooltip>
        ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) =>
        status === 'completed' ? (
          <Tooltip
            title={
              record.completed_at
                ? `Completed ${new Date(record.completed_at).toLocaleString()}${record.completed_by ? ` by ${record.completed_by}` : ''}`
                : 'Completed'
            }
          >
            <Tag icon={<CheckCircleOutlined />} color="success">
              Completed
            </Tag>
          </Tooltip>
        ) : (
          <Tag icon={<ClockCircleOutlined />} color="processing">
            Open
          </Tag>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Title level={4} style={{ margin: 0 }}>
        Feature Requests
      </Title>

      {/* ── Submit form ──────────────────────────────────────────────────── */}
      <Card
        title={
          <Space>
            <BulbOutlined />
            <span>Submit a Feature Request</span>
          </Space>
        }
        size="small"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ maxWidth: 720 }}>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter a title' }]}
              >
                <Input placeholder="Short description of the feature" maxLength={200} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="description" label="Description">
                <TextArea
                  placeholder="Any additional detail or context (optional)"
                  rows={3}
                  maxLength={2000}
                  showCount
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Submit Request
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* ── Requests table ───────────────────────────────────────────────── */}
      <Table
        columns={columns}
        dataSource={requests}
        rowKey="request_id"
        loading={loading}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        size="small"
        onRow={(record) => ({
          style: record.status === 'completed' ? { opacity: 0.55 } : {},
        })}
        locale={{ emptyText: 'No feature requests yet' }}
      />
    </Space>
  );
}
